import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'magere_cart';
const STORE_SLUG_KEY = 'magere_store_slug';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = sessionStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [storeSlug, setStoreSlug] = useState(() => {
    try {
      return sessionStorage.getItem(STORE_SLUG_KEY) || null;
    } catch {
      return null;
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  // Persist to sessionStorage whenever items change
  useEffect(() => {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Persist store slug
  useEffect(() => {
    if (storeSlug) {
      sessionStorage.setItem(STORE_SLUG_KEY, storeSlug);
    }
  }, [storeSlug]);

  const addItem = useCallback((product, quantity = 1, variation = null, currentStoreSlug = null, userType = null) => {
    if (currentStoreSlug) {
      setStoreSlug(currentStoreSlug);
    }
    const _candidate = (userType === 'pro' && product.price_pro) ? product.price_pro : (product.sale_price ?? product.price);
    if (!Number.isFinite(parseFloat(variation?.price ?? _candidate))) return;

    setItems(prev => {
      const existingIndex = prev.findIndex(
        item => item.product_id === product.id && 
        JSON.stringify(item.variation) === JSON.stringify(variation)
      );
      
      // Determine price: pro price if user is pro and product has price_pro
      const isPro = userType === 'pro';
      const basePrice = (isPro && product.price_pro) ? product.price_pro : (product.sale_price || product.price);

      // Check volume discounts
      let finalPrice = basePrice;
      const newQty = existingIndex >= 0 ? prev[existingIndex].quantity + quantity : quantity;
      if (product.volume_discounts?.length) {
        const sorted = [...product.volume_discounts].sort((a, b) => (b.min_qty || 0) - (a.min_qty || 0));
        for (const vd of sorted) {
          if (newQty >= (vd.min_qty || 0)) {
            finalPrice = vd.price;
            break;
          }
        }
      }

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        updated[existingIndex].price = finalPrice;
        updated[existingIndex].is_pro_price = isPro && !!product.price_pro;
        return updated;
      }
      
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: finalPrice,
        original_price: parseFloat(product.price),
        quantity,
        image: product.images?.[0],
        variation,
        is_pro_price: isPro && !!product.price_pro,
        tax_rate: typeof product.tax_rate === 'number' ? product.tax_rate : 5.5,
        weight: typeof product.weight === 'number' ? product.weight : null,
      }];
    });
  }, []);

  const removeItem = useCallback((productId, variation = null) => {
    setItems(prev => prev.filter(
      item => !(item.product_id === productId && 
        JSON.stringify(item.variation) === JSON.stringify(variation))
    ));
  }, []);

  const updateQuantity = useCallback((productId, quantity, variation = null) => {
    if (quantity <= 0) {
      removeItem(productId, variation);
      return;
    }
    
    setItems(prev => prev.map(item => {
      if (item.product_id === productId && 
          JSON.stringify(item.variation) === JSON.stringify(variation)) {
        return { ...item, quantity };
      }
      return item;
    }));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    sessionStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // TVA breakdown: TTC prices include VAT, so we extract it.
  // For each tax_rate group, vat_amount = ttc - (ttc / (1 + rate/100))
  const taxBreakdown = (() => {
    const groups = {};
    for (const it of items) {
      const rate = typeof it.tax_rate === 'number' ? it.tax_rate : 5.5;
      const ttc = (it.price || 0) * (it.quantity || 0);
      if (!groups[rate]) groups[rate] = { rate, ttc: 0, ht: 0, vat: 0 };
      groups[rate].ttc += ttc;
    }
    let totalVat = 0;
    for (const g of Object.values(groups)) {
      const ht = g.ttc / (1 + g.rate / 100);
      g.ht = ht;
      g.vat = g.ttc - ht;
      totalVat += g.vat;
    }
    return { groups: Object.values(groups).sort((a, b) => a.rate - b.rate), totalVat };
  })();

  const value = {
    items,
    isOpen,
    setIsOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    total,
    itemCount,
    storeSlug,
    setStoreSlug,
    taxBreakdown,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
